import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faArrowUpRight } from "@fortawesome/pro-solid-svg-icons/faArrowUpRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Modal, ModalProps } from "@mapmycustomers/ui";

import { showEntityView } from "@app/store/entityView/actions";
import {
  getBouncedEmail,
  getBouncedEmailEntity,
  isBouncedEmailModalVisible,
} from "@app/store/notification";
import {
  hideBouncedEmailModal,
  unsubscribeBouncedEmailEntity,
} from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import getLayoutModelByEntityType from "@app/util/layout/impl";

import messages from "./messages";
import styles from "./NotificationBouncedEmailModal.module.scss";
import UpdateEmailModal from "./UpdateEmailModal";

type NotificationBouncedEmailModalProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const NotificationBouncedEmailModal: React.FC<NotificationBouncedEmailModalProps> = ({
  email,
  entity,
  onHide,
  onShowEntityView,
  onUnsubscribe,
  open,
}) => {
  const intl = useIntl();

  const entityEditable = !!entity?.accessStatus?.update;
  const emailChanged = entity && email && entity.email !== email;

  const [updateEmailModalOpen, showUpdateEmailModal, hideUpdateEmailModal] = useBoolean();

  const entityLayout = useMemo(() => {
    return entity ? getLayoutModelByEntityType(entity.entity).getLayoutFor(entity) : undefined;
  }, [entity]);

  const emailField = useMemo(() => {
    return entity
      ? getFieldModelByEntityType(entity.entity).getByName(CommonFieldName.EMAIL)
      : entity;
  }, [entity]);

  const emailFieldSchema = useMemo(() => {
    return entityLayout?.schema.find((schema) => schema.field === CommonFieldName.EMAIL);
  }, [entityLayout]);

  const getModalDescription = () => {
    if (emailChanged) {
      if (entity.email) {
        return intl.formatMessage(messages.emailChangedDescription, { email: entity.email });
      }
      return intl.formatMessage(messages.noEmailDescription);
    }
    if (email && entity) {
      return intl.formatMessage(messages.emailNotDeliveredKnownEmailDescription, {
        email,
      });
    }
    return intl.formatMessage(messages.emailNotDeliveredUnknownEmailDescription, {
      email,
    });
  };

  const modalType: ModalProps["type"] = emailChanged ? "info" : "error";

  const handleUnsubscribeClick = useCallback(() => {
    onUnsubscribe();
    onHide();
  }, [onHide, onUnsubscribe]);

  const handleOpenRecordClick = useCallback(() => {
    onHide();
    if (entity) {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity,
      });
    }
  }, [entity, onHide, onShowEntityView]);

  const footerActions = useMemo(() => {
    const actions: { key: string; label: string; onClick: () => void }[] = [];
    if (emailChanged || !(entityEditable && entity?.subscribed)) {
      actions.push({
        key: "openRecord",
        label: intl.formatMessage(messages.openRecordButtonLabel),
        onClick: handleOpenRecordClick,
      });
    }
    if (!emailChanged && entity?.subscribed) {
      actions.push({
        key: "unsubscribe",
        label: intl.formatMessage(messages.unsubscribeButtonLabel),
        onClick: handleUnsubscribeClick,
      });
    }
    if (entityEditable && !emailChanged && emailField) {
      actions.push({
        key: "updateEmailButton",
        label: intl.formatMessage(messages.updateEmailButtonLabel),
        onClick: showUpdateEmailModal,
      });
    }
    return actions;
  }, [
    intl,
    emailField,
    emailChanged,
    entityEditable,
    entity,
    handleOpenRecordClick,
    handleUnsubscribeClick,
    showUpdateEmailModal,
  ]);

  const handleUpdateEmailModalHide = useCallback(() => {
    hideUpdateEmailModal();
    onHide();
  }, [hideUpdateEmailModal, onHide]);

  return (
    <>
      {open && !updateEmailModalOpen && (
        <Modal
          destroyOnClose
          footer={
            <>
              <Button onClick={onHide}>{intl.formatMessage(messages.cancelButtonLabel)}</Button>
              {footerActions.map(({ key, label, onClick }, index) => (
                <Button
                  key={key}
                  onClick={onClick}
                  type={index === footerActions.length - 1 ? "primary" : "filled"}
                >
                  {label}
                </Button>
              ))}
            </>
          }
          onCancel={onHide}
          open
          size="small"
          title={intl.formatMessage(
            emailChanged ? messages.emailChangedTitle : messages.emailNotDeliveredTitle
          )}
          type={modalType}
        >
          <Space direction="vertical" size="middle">
            <span>{getModalDescription()}</span>
            {entity?.subscribed && entityEditable && !emailChanged && (
              <Button
                className={styles.openRecordLinkButton}
                onClick={handleOpenRecordClick}
                rightIcon={<FontAwesomeIcon icon={faArrowUpRight} />}
                type="link"
              >
                {intl.formatMessage(messages.openRecordButtonLabel)}
              </Button>
            )}
          </Space>
        </Modal>
      )}
      {updateEmailModalOpen && entityLayout && emailField && (
        <UpdateEmailModal
          label={emailField.displayName}
          onHide={handleUpdateEmailModalHide}
          required={!!emailFieldSchema?.required}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  email: getBouncedEmail(state),
  entity: getBouncedEmailEntity(state),
  open: isBouncedEmailModalVisible(state),
});

const mapDispatchToProps = {
  onHide: hideBouncedEmailModal,
  onShowEntityView: showEntityView,
  onUnsubscribe: unsubscribeBouncedEmailEntity.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationBouncedEmailModal);
